import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

interface AssignToWarehouseProps {
  product?: any;
  onBack?: () => void;
}

interface WarehouseCheck {
  id: number;
  label: string;
  checked: boolean;
}

const initialWarehouseList: WarehouseCheck[] = [
  { id: 1, label: '1 - Vashi Depo - 1537', checked: false },
  { id: 2, label: '2 - Atharva Distributors - 6000222', checked: false },
  { id: 3, label: '3 - Ghule Dairy - 6000230', checked: false },
  { id: 4, label: '4 - Harshad Patil-Kandivali - 6000966', checked: false },
  { id: 5, label: '5 - Kanchan Dairy Farm-Malad - 6000636', checked: false },
  { id: 6, label: '6 - Lata Enterprises - 6000758', checked: false },
  { id: 7, label: '7 - MAURYA DAIRY - 6000585', checked: false },
  { id: 8, label: '8 - Maurya Enterprises-Malad - 6000889', checked: false },
  { id: 9, label: '9 - Samant Milk Supplier - 6000364', checked: false },
  { id: 10, label: '10 - Shiva Milk Agency-Goregaon - 6000868', checked: false },
  { id: 11, label: '11 - SAGAR DISTRIBUTORS-GOREGAON - 6000976', checked: false },
  { id: 12, label: '12 - Abhinav Dairy - 6000046', checked: false },
  { id: 13, label: '13 - Jai Ambe General And Milk Store - 6000924', checked: false },
  { id: 14, label: '14 - Jai Hanuman Dairy - 6000709', checked: false },
  { id: 15, label: '15 - NIRMALA MILK CENTRE-JOGESHWARI - 6000984', checked: false },
  { id: 16, label: '16 - Om Gura Kripa Dairy - 6000509', checked: false },
  { id: 17, label: '17 - Om Milk Centre-Andheri - 6000670', checked: false },
  { id: 18, label: '18 - Pooja Dairy Farm - 6000951', checked: false },
  { id: 19, label: '19 - Prakash Dairy - 6000052', checked: false },
  { id: 20, label: '20 - Prakash Milk Distributor - Andheri - 6000665', checked: false },
  { id: 21, label: '21 - Saket Avinash Ramachandra - Andheri - 6000099', checked: false }
];

const AssignToWarehouse: React.FC<AssignToWarehouseProps> = ({ product: propProduct, onBack }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const product = propProduct ?? location.state?.product;
  const handleBack = () => {
    if (onBack) { onBack(); } else { navigate(-1); }
  };
  const [selectedZone, setSelectedZone] = useState('');
  const [mrp, setMrp] = useState(product?.mrp || '');
  const [price, setPrice] = useState('0');
  const [quantity, setQuantity] = useState('');
  const [warehouses, setWarehouses] = useState<WarehouseCheck[]>(initialWarehouseList);

  const productCode = product?.skuCode || '85000000';
  const productName = product?.name || 'GOW. GOLD COW MILK 6000 ML';

  // Toggle individual checkbox
  const handleCheckboxToggle = (id: number) => {
    setWarehouses(prev => 
      prev.map(wh => wh.id === id ? { ...wh, checked: !wh.checked } : wh)
    );
  };

  // Select / Deselect All logic
  const isAllChecked = useMemo(() => warehouses.every(wh => wh.checked), [warehouses]);

  const handleToggleAll = () => {
    setWarehouses(prev => 
      prev.map(wh => ({ ...wh, checked: !isAllChecked }))
    );
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedCount = warehouses.filter(w => w.checked).length;
    if (selectedCount === 0) {
      alert('Please select at least one Warehouse/Distributor.');
      return;
    }
    alert(`Successfully assigned product to ${selectedCount} warehouses/distributors!`);
    handleBack();
  };

  return (
    <div className="space-y-6">
      
      {/* Sub-Header Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        
        {/* Left Back and Product Title */}
        <div className="flex items-center gap-3">
          <button onClick={handleBack}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0 whitespace-pre-wrap md:whitespace-nowrap">
            Assign to Warehouse ({productCode} - {productName})
          </h1>
        </div>
      </div>

      {/* Side by Side Content Card */}
      <div className="bg-white rounded-2xl p-4">
        
        <form onSubmit={handleFormSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* LHS FORM FIELDS */}
          <div className="space-y-5">
            
            {/* Select Zone */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-[#1F1F1F] block">Select Zone</label>
              <select
                value={selectedZone}
                onChange={(e) => setSelectedZone(e.target.value)}
                className="w-full rounded-xl  border border-[#828282] px-4 py-2 text-xs outline-none transition-colors bg-gray-200 cursor-pointer appearance-none pr-8 bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%232C3E50%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:14px_14px] bg-[right_12px_center] bg-no-repeat"
              >
                <option value="">Please Select</option>
                <option value="MUM0484">MUM0484</option>
                <option value="MUM0485">MUM0485</option>
                <option value="PUN0129">PUN0129</option>
                <option value="NSK0921">NSK0921</option>
              </select>
            </div>

            {/* MRP */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-[#1F1F1F] block">MRP*</label>
              <input
                type="text"
                required
                value={mrp}
                onChange={(e) => setMrp(e.target.value)}
                className="w-full border border-[#828282] rounded-xl px-4 py-2 text-xs font-semibold focus:border-slate-400 outline-none transition-colors"
              />
            </div>

            {/* Price */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-[#1F1F1F] block mb-1">Price</label>
              <input
                type="text"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                className="w-full border border-[#828282] rounded-xl px-4 py-2 text-xs font-semibold outline-none transition-colors"
              />
            </div>

            {/* Quantity */}
            <div className="space-y-1">
              <label className="text-xs font-semibold text-[#1F1F1F] block mb-1">Quantity (cases)</label>
              <input
                type="text"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="w-full border border-[#828282] rounded-xl px-4 py-2 text-xs font-semibold outline-none transition-colors"
              />
            </div>

            {/* Submit button on the LHS to complete the form logic */}
            <button
              type="submit"
              className="w-full mt-3 py-2 bg-[#D49804] text-white text-sm font-medium rounded-full transition-all tracking-wider cursor-pointer"
            >
              Assign To Warehouse
            </button>

          </div>

          {/* RHS SCROLLABLE CHECKLIST */}
          <div className="flex flex-col h-full border-l border-slate-100 pl-0 lg:pl-8">
            
            <label className="text-sm font-semibold text-[#1F1F1F] block mb-3">
              Select WAREHOUSE/DISTRIBUTOR
            </label>

            {/* Select/Deselect All Checkbox */}
            <label className="flex items-center gap-3 text-xs text-[#1F1F1F] cursor-pointer select-none pb-1 mb-3 border-b border-gray-300">
              <input
                type="checkbox"
                checked={isAllChecked}
                onChange={handleToggleAll}
                className="w-4 h-4 rounded border-[#828282] go_erp-text"
              />
              <span>- Select/Deselect All</span>
            </label>

            {/* Scrollable List */}
            <div className="max-h-[380px] overflow-y-auto pr-2 space-y-3 hide-scrollbar">
              {warehouses.map((wh) => (
                <label 
                  key={wh.id} 
                  className="flex items-center gap-3 text-xs text-[#1F1F1F] cursor-pointer select-none transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={wh.checked}
                    onChange={() => handleCheckboxToggle(wh.id)}
                    className="w-4 h-4 rounded border-[#828282] go_erp-text "
                  />
                  <span>{wh.label}</span>
                </label>
              ))}
            </div>

          </div>

        </form>

      </div>

    </div>
  );
};

export default AssignToWarehouse;